import Axios from 'api/client';
import { ModelType } from 'components/filter/types-legacy';
import { Segment } from 'types/domain';
import { FilterParams } from 'types/params';
import { OutboundFilters } from 'types/request';

interface CreateSegmentsParams {
    accountId: number;
    name: string;
    filters: OutboundFilters;
    type: ModelType;
}

interface UpdateSegmentsParams extends CreateSegmentsParams {
    segmentId: number;
}

interface DeleteSegmentParams {
    accountId: number;
    segmentId: number;
}

interface CreateOrUpdateSegmentParams extends CreateSegmentsParams {
    segmentId?: number;
}

interface PinSegmentParams {
    accountId: number;
    segmentId: number;
}

const fetchSegments = (accountId: number, type: ModelType, params: FilterParams = {}) => {
    const { filters: _, ...restParams } = params;

    return Axios.get<Segment[]>(`/accounts/${accountId}/segments`, {
        params: {
            table: getTableNameFromModelType(type),
            ...restParams,
        },
    }).then(response => response.data);
};

const createSegment = ({ accountId, ...segmentBody }: CreateSegmentsParams) =>
    Axios.post<Segment>(`/accounts/${accountId}/segments`, transformBody(segmentBody)).then(
        response => response.data,
    );

const updateSegment = ({ accountId, segmentId, ...segmentBody }: UpdateSegmentsParams) =>
    Axios.patch<Segment>(
        `/accounts/${accountId}/segments/${segmentId}`,
        transformBody(segmentBody),
    ).then(response => response.data);

const deleteSegment = ({ accountId, segmentId }: DeleteSegmentParams) =>
    Axios.delete<Segment>(`/accounts/${accountId}/segments/${segmentId}`).then(
        response => response.data,
    );

const createOrUpdateSegment = ({
    accountId,
    segmentId,
    ...segmentBody
}: CreateOrUpdateSegmentParams) => {
    if (segmentId) {
        return updateSegment({ accountId, segmentId, ...segmentBody });
    }

    return createSegment({ accountId, ...segmentBody });
};

const pinSegment = ({ accountId, segmentId }: PinSegmentParams) =>
    Axios.post(`/accounts/${accountId}/segments/${segmentId}/pin`);

const unpinSegment = ({ accountId, segmentId }: PinSegmentParams) =>
    Axios.post(`/accounts/${accountId}/segments/${segmentId}/unpin`);

const transformBody = <T extends { type: ModelType }>({ type, ...params }: T) => ({
    ...params,
    table: getTableNameFromModelType(type),
});

const getTableNameFromModelType = (type: ModelType) => {
    switch (type) {
        case 'plans':
            return 'transaction_plans';
        default:
            return type;
    }
};

export {
    fetchSegments,
    createSegment,
    updateSegment,
    deleteSegment,
    createOrUpdateSegment,
    pinSegment,
    unpinSegment,
};
